import type { CityConfig } from "../city-template/types";

export const fallonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Fallon",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "fallon",
  route: "/cities/fallon",
  title: "Emergency Passport Fallon NV | Expedited Passport Renewal Churchill County | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport renewal in Fallon, NV — 24-48 hour processing available. Fast passport services for Fallon, Churchill County, NAS Fallon personnel, and Hwy 95 corridor travelers. A+ BBB rated, State Dept. registered courier. No agency visit required.",
  h1: "Emergency & Expedited Passport Services in Fallon, NV",
  heroSubtitle: "Passport expiring before your next deployment or trip? Fast Passport Center helps Fallon, NAS Fallon military personnel, and Churchill County travelers get expedited passports in as little as 24 hours. No agency visit required — Nevada has no local passport agency.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Fallon%20Nevada%20agricultural%20landscape%20with%20the%20Stillwater%20Range%20in%20the%20distance%20and%20Lahontan%20Valley%20farmland%20under%20a%20dramatic%20desert%20sky%2C%20rural%20Churchill%20County%20town%20with%20wide%20streets%20and%20historic%20downtown%2C%20high%20desert%20oasis%20with%20irrigation%20canals%20and%20green%20alfalfa%20fields%20contrasting%20with%20sagebrush%2C%20golden%20hour%20lighting%20illuminating%20the%20Carson%20Sink%20region%20with%20distant%20mountain%20silhouettes%2C%20no%20text%20or%20people%20visible&width=1800&height=700&seq=fallon-nv-hero-001&orientation=landscape",
  heroImageAlt: "Fallon Nevada agricultural landscape with Lahontan Valley farmland and Stillwater Range",
  geoPosition: "39.4736;-118.7771",
  geoPlacename: "Fallon, Nevada",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "San Francisco Passport Agency",
    address: "95 Hawthorne St, Suite 600, San Francisco, CA 94105",
    distance: "Located in San Francisco — approximately 280 miles from Fallon via I-80 and US-95",
  },
  nearbyCitiesZips: [
    { name: "Fallon", zips: "89406" },
    { name: "Fernley", zips: "89408" },
    { name: "Silver Springs", zips: "89429" },
    { name: "Lovelock", zips: "89419" },
    { name: "Yerington", zips: "89447" },
    { name: "Dayton", zips: "89403" },
    { name: "Carson City", zips: "89701" },
    { name: "Reno", zips: "89501" },
    { name: "Sparks", zips: "89431" },
    { name: "Winnemucca", zips: "89445" },
    { name: "Hawthorne", zips: "89415" },
    { name: "Battle Mountain", zips: "89820" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Fallon and NAS Fallon departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — ship from any UPS in Fallon, Fernley, or Reno" },
  ],
  internalLinks: [
    { name: "Emergency passport services in Reno", route: "/cities/reno" },
    { name: "Fast passport renewal in Fernley", route: "/cities/fernley" },
    { name: "Expedited passport help in Carson City", route: "/cities/carson-city" },
    { name: "Urgent passport services in Sacramento", route: "/cities/sacramento" },
    { name: "Same-day passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Passport courier services in Northern Nevada", route: "/states/nevada" },
  ],
  fpcOfficeAddress: "100 W Liberty St, Reno, NV 89501",
  fpcOfficeNote: "Fast Passport Center serves Fallon remotely via secure overnight courier. We do not have a walk-in office in Fallon — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Fallon, NAS Fallon personnel, and Churchill County residents.",
  fpcOfficeDistance: "Serving all of Fallon, NAS Fallon, and rural Churchill County",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Fallon and Churchill County. Home to Naval Air Station Fallon — the U.S. Navy's premier tactical air warfare training center (TOPGUN) — Fallon has a significant military and defense contractor population with frequent international travel needs. We provide fast passport processing for this unique community.",
  nearbyCitiesIntro: "We serve all Fallon neighborhoods and Churchill County communities, plus nearby areas including Fernley, Silver Springs, Lovelock, Yerington, and Dayton. Our overnight shipping labels work from any UPS location — including the UPS Store in Fallon itself.",
  howItWorksIntro: "Most Fallon travelers complete everything online and ship documents overnight. No need to drive to Reno, Sacramento, or San Francisco. Our emergency passport process delivers fast results for Churchill County and NAS Fallon travelers.",
  mistakesIntro: "Fallon and Churchill County residents face unique challenges with passport processing — especially military personnel with deployment timelines. Here are the most common passport mistakes we see in the Lahontan Valley.",
  testimonialsIntro: "Fallon, NAS Fallon, and Churchill County travelers trust us when their international departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Fallon? Here are the most common ones we hear from Churchill County, NAS Fallon, and Hwy 95 corridor travelers.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  dmvWebsite: "https://dmv.nv.gov",
  dmvLabel: "Nevada DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Nearest agency is San Francisco (280 miles)",
        "Not practical from Fallon",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips and deployments",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Fallon and NAS Fallon travelers with immediate departure needs — as fast as 24 hours for urgent flights or military deployment requirements.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Military deployment support available"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance for Fallon and Churchill County residents.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance — critical for military families with frequent moves and international travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes — common for military families.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Fallon Nevada travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens in Fallon NV and Churchill County." },
    { name: "New Passport", description: "First-time passport application assistance for Fallon and Churchill County residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Cmdr. James R.",
      location: "NAS Fallon, NV",
      rating: 5,
      text: "Navy officer with a sudden deployment timeline change. My family needed passports fast for overseas orders. Fast Passport Center processed all four of us in 3 days. The military community here swears by this service.",
      service: "Emergency Passport",
    },
    {
      name: "Patricia L.",
      location: "Fallon, NV",
      rating: 5,
      text: "Defense contractor at NAS Fallon who needed a passport for a conference in Australia. They got it done in 48 hours. The overnight shipping from Fallon worked perfectly.",
      service: "Passport Renewal",
    },
    {
      name: "The Martinez Family",
      location: "Fallon, NV",
      rating: 5,
      text: "Agricultural family with a trip to Mexico for a wedding. Four passports needed on short notice — all arrived in 4 days. The dedicated agent made everything so easy. Truly a lifesaver for rural Nevada families.",
      service: "New Passport",
    },
    {
      name: "Sgt. David K.",
      location: "Churchill County, NV",
      rating: 5,
      text: "Marine on leave in Fallon — realized my passport was expired 5 days before returning to Okinawa. Fast Passport Center got it renewed in 36 hours. I reported for duty on time. Outstanding service.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "fallon-1",
      name: "Fallon Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1150 W Williams Ave",
      city: "Fallon",
      state: "NV",
      zip: "89406",
      phone: "(775) 423-4675",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Fallon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Churchill County facility. Full passport acceptance services. Appointments recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "fallon-2",
      name: "Churchill County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "155 N Taylor St",
      city: "Fallon",
      state: "NV",
      zip: "89406",
      phone: "(775) 423-6028",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Downtown Fallon",
      website: "https://www.churchillcountynv.gov/",
      websiteLabel: "Churchill County Clerk",
      notes: "County government passport acceptance. Serves all Churchill County. Convenient downtown location.",
      icon: "ri-government-line",
    },
    {
      id: "fallon-3",
      name: "NAS Fallon Passport Office",
      type: "Military Installation Passport Services",
      address: "4755 Pasture Rd, Bldg 300",
      city: "Fallon",
      state: "NV",
      zip: "89496",
      phone: "(775) 426-2200",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM (active duty only)",
      distance: "NAS Fallon — 6 miles southeast",
      website: "https://www.cnic.navy.mil/fallon",
      websiteLabel: "NAS Fallon Official Site",
      notes: "Limited to active duty military and DoD civilians. For official and no-fee passports only. Not available for tourist passports.",
      icon: "ri-shield-line",
    },
    {
      id: "fallon-4",
      name: "Fernley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "440 E Main St",
      city: "Fernley",
      state: "NV",
      zip: "89408",
      phone: "(775) 575-2421",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "25 miles west of Fallon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative facility 25 miles west. Convenient if you're traveling toward Reno.",
      icon: "ri-mail-line",
    },
    {
      id: "fallon-5",
      name: "Reno Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Virginia St",
      city: "Reno",
      state: "NV",
      zip: "89501",
      phone: "(775) 788-0900",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "60 miles west of Fallon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Largest passport facility in Northern Nevada. Full services with appointment. 60 miles via US-50 and I-80.",
      icon: "ri-mail-line",
    },
    {
      id: "fallon-6",
      name: "Carson City Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1111 S Roop St",
      city: "Carson City",
      state: "NV",
      zip: "89701",
      phone: "(775) 884-2300",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "55 miles southwest of Fallon",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "State capital facility. Full passport services. 55 miles via US-50 — a scenic drive through the high desert.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 280 miles to San Francisco", fpc: "No — ship from Fallon UPS or home" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Fallon has limited photo options. Passport photos must be exactly 2×2 inches with a white or off-white background — don't settle for a generic drugstore photo setup.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or wrong form type are the #1 reason for rejection at the Fallon Post Office and Churchill County Clerk.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "Military Deployment Timeline Conflicts",
      description: "NAS Fallon personnel often have compressed timelines. Standard processing (8–11 weeks) won't work for sudden deployment orders — you need expedited courier service.",
    },
    {
      icon: "ri-road-map-line",
      title: "Underestimating Mountain Travel",
      description: "Driving to the San Francisco Passport Agency means crossing the Sierra Nevada — 280 miles and 5+ hours each way. Our courier service eliminates this completely.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent for Military Families",
      description: "When one parent is deployed, getting notarized consent for a child's passport becomes complex. Our agents understand military family dynamics and can guide you through alternatives.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Nevada has no local passport agency. From Fallon, mail transit alone adds days to standard processing. Start expedited at least 3 weeks before travel, especially for military moves.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at the UPS Store in Fallon or the Reno area." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Fallon address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Fallon, NV?",
      a: "Fast Passport Center offers emergency passport processing in Fallon in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities. Nevada has no local passport agency — the nearest is San Francisco, 280 miles away — so our courier service is by far the fastest option for Fallon and Churchill County travelers.",
    },
    {
      q: "Do you serve NAS Fallon military personnel?",
      a: "Absolutely. NAS Fallon is home to the Naval Aviation Warfighting Development Center (TOPGUN) and we serve all military personnel, DoD civilians, and their families. We understand deployment timelines and the need for fast, reliable passport processing. Our 24–48 hour emergency service is ideal for sudden orders or training-related international travel.",
    },
    {
      q: "What is the nearest passport agency to Fallon?",
      a: "The nearest passport agency to Fallon is the San Francisco Passport Agency, approximately 280 miles away — about a 5-hour drive over the Sierra Nevada. Fast Passport Center's courier service means you never have to make that drive, which is especially important for military families with unpredictable schedules.",
    },
    {
      q: "Does Fallon have a passport acceptance facility?",
      a: "Yes. The Fallon Post Office at 1150 W Williams Ave and the Churchill County Clerk at 155 N Taylor St both offer passport acceptance services. For military personnel, NAS Fallon has a passport office but it's limited to official/no-fee passports only. For tourist passports, use the Fallon Post Office or our courier service.",
    },
    {
      q: "What about agricultural and farming families in Churchill County?",
      a: "Churchill County is Nevada's agricultural heartland — known for Hearts of Gold cantaloupes and alfalfa farming. Fast Passport Center serves all Fallon and Churchill County farming families with fast passport processing. Our overnight shipping system works from any location, including the UPS Store in downtown Fallon.",
    },
    {
      q: "Can I fly internationally from Fallon Municipal Airport?",
      a: "Fallon Municipal Airport primarily serves general aviation. For commercial international flights, Reno-Tahoe International Airport is about 60 miles from Fallon via US-50 — about an hour drive. Fast Passport Center ensures your passport is ready before departure.",
    },
    {
      q: "How much does expedited passport service cost in Fallon?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by speed — emergency 24–48 hour, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your departure or deployment date.",
    },
  ],
  photoLocations: [
    { name: "Fallon Post Office", address: "1150 W Williams Ave, Fallon, NV 89406", hours: "Mon–Fri: 9AM–4PM (photo by appointment)", website: "https://tools.usps.com/find-location.htm" },
    { name: "Walmart — Fallon", address: "2333 Reno Hwy, Fallon, NV 89406", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "CVS Pharmacy — Fallon", address: "2160 W Williams Ave, Fallon, NV 89406", hours: "Mon–Sun: 8AM–10PM", website: "https://www.cvs.com/" },
    { name: "UPS Store — Fallon", address: "2020 Reno Hwy, Fallon, NV 89406", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–3PM", website: "https://www.theupsstore.com/" },
    { name: "Walmart — Fernley", address: "1550 E Newlands Dr, Fernley, NV 89408", hours: "Mon–Sun: 6AM–11PM", website: "https://www.walmart.com/" },
    { name: "Walgreens — Sparks", address: "1075 Baring Blvd, Sparks, NV 89434", hours: "Mon–Sun: 7AM–11PM", website: "https://www.walgreens.com/" },
    { name: "FedEx Office — Reno", address: "1201 Terminal Way, Reno, NV 89502", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM", website: "https://www.fedex.com/" },
    { name: "Costco — Sparks", address: "989 Sparks Blvd, Sparks, NV 89434", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM", website: "https://www.costco.com/" },
  ],
  dmvLocations: [
    {
      name: "Nevada DMV — Fallon Express",
      address: "111 Industrial Way, Fallon, NV 89406",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Closest DMV to Fallon. Express office — call ahead for passport-related documentation and REAL ID services.",
      featured: true,
    },
    {
      name: "Nevada DMV — Carson City",
      address: "555 Wright Way, Carson City, NV 89711",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Primary Northern Nevada DMV. 55 miles from Fallon. REAL ID processing. Online appointment recommended.",
      featured: true,
    },
    {
      name: "Nevada DMV — Sparks",
      address: "1775 Hymer Ave, Sparks, NV 89431",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service DMV in Sparks. About 60 miles from Fallon. REAL ID services available.",
      featured: false,
    },
    {
      name: "Churchill County Clerk — Fallon",
      address: "155 N Taylor St, Fallon, NV 89406",
      hours: "Mon–Fri: 8AM–5PM",
      note: "County government services for Churchill County residents. Passport acceptance available.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "RNO",
      name: "Reno-Tahoe International Airport",
      distance: "60 miles from Fallon via US-50",
      address: "2001 E Plumb Ln, Reno, NV 89502",
      note: "Primary airport for Fallon and Northern Nevada. Direct service to Mexico and connecting international flights. 60-mile drive via US-50.",
      flightUrl: "https://www.renoairport.com/",
      flightLabel: "Reno-Tahoe Airport",
      featured: true,
    },
    {
      code: "SFO",
      name: "San Francisco International Airport",
      distance: "280 miles from Fallon via I-80",
      address: "San Francisco, CA 94128",
      note: "Primary West Coast hub with extensive international connections. Home to the San Francisco Passport Agency.",
      flightUrl: "https://www.flysfo.com/",
      flightLabel: "SFO Airport",
      featured: false,
    },
    {
      code: "SMF",
      name: "Sacramento International Airport",
      distance: "190 miles from Fallon via I-80",
      address: "6900 Airport Blvd, Sacramento, CA 95837",
      note: "Alternative regional hub accessible via I-80 and US-50. Good option for west coast and Pacific connections.",
      flightUrl: "https://www.sacramento.aero/smf/",
      flightLabel: "Sacramento Airport",
      featured: false,
    },
  ],
};